import { useState, useEffect } from 'react';
import { ChevronDown, Loader2, Paperclip } from 'lucide-react';
import api from '../../services/api';
import { getInitials, getAvatarColor } from '../../lib/avatar';
import { FOLDERS } from '../../lib/folders';
import EmailBody from './EmailBody';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const folderLabel = (imap) => FOLDERS.find((f) => f.imap === imap)?.name || imap;

// Konuşmadaki diğer mailler: başlıkları açık, gövdeleri tıklanınca yüklenir (açık maili değiştirmez)
export const ThreadMessages = ({ items, currentId, currentFolder }) => {
  const [openKey, setOpenKey] = useState(null);
  const [bodies, setBodies] = useState({});

  useEffect(() => { setOpenKey(null); setBodies({}); }, [currentId, currentFolder]);

  const others = items.filter((m) => !(m.id === currentId && m.folder === currentFolder));
  if (others.length === 0) return null;

  const toggle = async (m) => {
    const key = `${m.folder}:${m.id}`;
    if (openKey === key) { setOpenKey(null); return; }
    setOpenKey(key);
    if (bodies[key]) return;
    try {
      const detail = await api.getMessage(m.id, m.folder);
      setBodies((prev) => ({ ...prev, [key]: { html: detail.body_html, text: detail.body_text } }));
    } catch (err) {
      setBodies((prev) => ({ ...prev, [key]: { error: err.message || 'Mail yüklenemedi' } }));
    }
  };

  return (
    <section className="mb-6" data-testid="thread-section">
      <h2 className="text-sm font-semibold mb-2">Konuşmadaki diğer mailler ({others.length})</h2>
      <ul className="space-y-2">
        {others.map((m) => {
          const key = `${m.folder}:${m.id}`;
          const open = openKey === key;
          const body = bodies[key];
          const name = m.from_addr?.name || m.from_addr?.email || 'Bilinmeyen';
          return (
            <li key={key} className="rounded-lg border border-border overflow-hidden" data-testid={`thread-item-${m.id}`}>
              <button onClick={() => toggle(m)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-secondary/60" aria-expanded={open}>
                <span className={`h-8 w-8 shrink-0 rounded-full text-xs font-semibold flex items-center justify-center ${getAvatarColor(m.from_addr?.email || name)}`}>
                  {getInitials(m.from_addr?.name, m.from_addr?.email)}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${m.is_read ? 'font-medium' : 'font-semibold'}`}>{name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatDate(m.date)}</span>
                  </span>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{m.preview || m.subject}</span>
                    {m.has_attachment && <Paperclip className="h-3 w-3 shrink-0" />}
                    <span className="shrink-0 rounded bg-secondary px-1.5 py-px">{folderLabel(m.folder)}</span>
                  </span>
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
              {open && (
                <div className="border-t border-border bg-white">
                  {!body ? (
                    <div className="flex justify-center p-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                  ) : body.error ? (
                    <p className="p-4 text-sm text-destructive">{body.error}</p>
                  ) : body.html ? (
                    <EmailBody key={key} html={body.html} testId={`thread-body-${m.id}`} />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans text-sm p-4 text-gray-800">{body.text || 'İçerik yok'}</pre>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ThreadMessages;
